// Beosztás (position) create/edit — jobb oldali beúszó FormSheet (a department/team form-sheet testvére).
// A szervezeti fa LEGALSÓ szintje. Egy komponens KÉT módban: `position == null` → LÉTREHOZÁS (POST),
// különben SZERKESZTÉS (PATCH If-Match/OCC). A név/leírás KÉTNYELVŰ.
//
// A `teamId` KÖTELEZŐ — a CSAPAT-választó a csapat-lista referencia-lekérdezéséből töltődik (nagy limit,
// name ASC), és a RÉSZLEGEK szerint CSOPORTOSÍTVA listáz: a beosztás-felvevő így látja, melyik részleg alá
// esik a választott csapat anélkül, hogy külön mezőt kellene kitöltenie. Részleg-mező SZÁNDÉKOSAN nincs:
// az a csapatból következik (egy igazság-forrás — két mező szinkronban tartása némán elcsúszna).
//
// A `slug` GENERÁLT (nem form-mező; a szerver a nameEn-ből). A validálás forrás-igazsága a
// @atlas/contracts PositionWriteBodySchema (a features/org-structure/form.ts resolver-én át).
import { type OrgPositionRowSchema } from '@atlas/contracts';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { BilingualField } from '@/components/shared/bilingual-field';
import { SelectField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { SlugHint } from '@/components/shared/slug-hint';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { positionKeys, teamKeys } from '@/features/org-structure/api';
import {
  buildPositionResolver,
  EMPTY_POSITION_FORM,
  toPositionBody,
  type PositionFormValues,
} from '@/features/org-structure/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type PositionRow = z.infer<typeof OrgPositionRowSchema>;

export interface PositionFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a szerkesztendő beosztás (hordozza az OCC `version`-t + az előtöltést). */
  position: PositionRow | null;
}

export function PositionFormSheet({
  open,
  onOpenChange,
  position,
}: PositionFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEdit = position !== null;
  const isEn = i18n.language.toLowerCase().startsWith('en');

  // Csapat-opciók a lista referencia-lekérdezéséből (nagy limit — a belső csapat-szám kicsi, egy lap elég).
  const teamQ = api.orgTeams.useQuery(teamKeys.refs(), {
    query: { limit: 200, sort: 'name', order: 'asc' },
  });
  const teams = teamQ.data?.status === 200 ? teamQ.data.body.teams : [];

  // A választó a csapatokat a RÉSZLEGÜK szerint csoportosítva kínálja (SelectGroup + SelectLabel). A
  // részleg nélküli csapat elvileg nem létezhet (a szerver kötelezővé teszi), de ha mégis előfordulna,
  // egy „besorolatlan" csoportba esik — nem tűnhet el némán a listából.
  const teamGroups = useMemo(() => {
    const byDept = new Map<string, { value: string; label: string }[]>();
    for (const tm of teams) {
      const deptLabel = tm.departmentName ?? t('orgStructure.teams.noDepartment');
      const list = byDept.get(deptLabel) ?? [];
      list.push({ value: tm.id, label: isEn ? tm.nameEn : tm.nameHu });
      byDept.set(deptLabel, list);
    }
    return [...byDept.entries()]
      .sort(([a], [b]) => a.localeCompare(b, i18n.language))
      .map(([label, options]) => ({ label, options }));
  }, [teams, isEn, t, i18n.language]);

  const form = useForm<PositionFormValues>({
    resolver: buildPositionResolver(t),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_POSITION_FORM,
  });
  const { reset, handleSubmit, setError } = form;

  useEffect(() => {
    if (position) {
      reset({
        nameHu: position.nameHu,
        nameEn: position.nameEn,
        teamId: position.teamId ?? '',
        descriptionHu: position.descriptionHu ?? '',
        descriptionEn: position.descriptionEn ?? '',
      });
    } else {
      reset(EMPTY_POSITION_FORM);
    }
  }, [position, reset]);

  const mutation = useEntityMutation<PositionFormValues>({
    mutationFn: (values) => {
      const body = toPositionBody(values);
      return isEdit
        ? api.positionUpdate.mutation({
            params: { id: position.id },
            body,
            headers: { 'if-match': String(position.version) },
          })
        : api.positionCreate.mutation({ body });
    },
    // a beosztás-lista mellett a CSAPAT-sorok `positions[]` listája is elavul (új/áthelyezett beosztás)
    invalidateKeys: [positionKeys.lists(), teamKeys.lists()],
    onFieldErrors: (fieldName, messages) => {
      if (fieldName in EMPTY_POSITION_FORM) {
        setError(fieldName as keyof PositionFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isEdit
        ? t('toast:org_structure.positions.updated')
        : t('toast:org_structure.positions.created'),
      conflict: t('toast:org_structure.positions.conflict'),
      forbidden: t('toast:org_structure.positions.forbidden'),
      validation: t('toast:org_structure.positions.validation'),
      error: t('toast:org_structure.positions.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) });
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_POSITION_FORM);
        onOpenChange(o);
      }}
      title={
        isEdit ? t('orgStructure.positions.editTitle') : t('orgStructure.positions.createTitle')
      }
      description={
        isEdit ? <SlugHint slug={position.slug} /> : t('orgStructure.positions.createDesc')
      }
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('orgStructure.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('orgStructure.saving') : t('orgStructure.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <SelectField<PositionFormValues>
          name="teamId"
          label={t('orgStructure.fields.team')}
          required
          placeholder={t('orgStructure.fields.teamPlaceholder')}
          groups={teamGroups}
        />
        <BilingualField<PositionFormValues>
          variant="input"
          label={t('orgStructure.fields.name')}
          nameHu="nameHu"
          nameEn="nameEn"
          required
        />
        <BilingualField<PositionFormValues>
          variant="textarea"
          label={t('orgStructure.fields.description')}
          nameHu="descriptionHu"
          nameEn="descriptionEn"
          rows={2}
        />
      </Form>
    </FormSheet>
  );
}
